// Shared attachment state + upload pipeline for the reply composer and the
// new-ticket compose dialog.
//
// Sources of files:
//   1. Click the paperclip → file picker
//   2. Paste an image (Ctrl/Cmd-V on screenshots / clipboard images)
//   3. Drag & drop onto the textarea
//
// Upload happens on Send, but not here: Send hands the files to the outbox
// (lib/send-outbox.ts) and the send pipeline (lib/outbound-send.ts) pushes
// each one to Supabase Storage (the `ticket-attachments` public bucket also
// used by workflow 01). The resulting `{filename, contentType, size, url}`
// array goes in the workflow payload; workflows 12 and 15 fetch each URL and
// embed the bytes as multipart/mixed parts in the outgoing MIME message.
//
// Why upload in the SPA (not the workflow): keeps the n8n payload small.
// This hook only holds the chips — the files, their previews, and the
// size / count limits.

import { toast } from 'sonner';
import { useCallback, useState } from 'react';

export interface PendingAttachment {
  /** Local id used for keying + remove buttons. Generated client-side. */
  localId: string;
  file: File;
  /** Object URL for image preview in the chip. Revoked on remove. */
  previewUrl: string | null;
  /** Set after upload — what we send to the workflow. */
  uploadedUrl: string | null;
  /** Upload progress: 'idle' before, 'uploading' during, 'done' after, 'error' on failure. */
  state: 'idle' | 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25MB per file (Gmail's send limit)
const MAX_FILES = 10;

export function useComposerAttachments() {
  const [items, setItems] = useState<PendingAttachment[]>([]);

  // `add` used to drop rejected files on the floor: `if (remaining <= 0) return
  // prev` and a `.filter()` on size, with no toast anywhere in this file. Drag
  // in 50 screenshots and 10 attached while 40 vanished; drop a 30MB video and
  // literally nothing happened, which is indistinguishable from a broken drop
  // zone. NotesPanel already did this correctly three files away.
  //
  // Rejections are reported here rather than returned to each call site,
  // because all four call sites want the identical message.
  const add = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    setItems((prev) => {
      const remaining = MAX_FILES - prev.length;
      const overCount = arr.slice(Math.max(0, remaining));
      const candidates = remaining > 0 ? arr.slice(0, remaining) : [];
      const tooBig = candidates.filter((f) => f.size > MAX_FILE_BYTES);
      const empty = candidates.filter((f) => f.size === 0);
      const accepted = candidates.filter((f) => f.size > 0 && f.size <= MAX_FILE_BYTES);

      const problems: string[] = [];
      if (overCount.length > 0) {
        problems.push(
          `${overCount.length} over the ${MAX_FILES}-file limit`,
        );
      }
      if (tooBig.length > 0) {
        problems.push(
          `${tooBig.length} over ${humanFileSize(MAX_FILE_BYTES)} (${tooBig
            .map((f) => f.name)
            .slice(0, 3)
            .join(', ')}${tooBig.length > 3 ? '…' : ''})`,
        );
      }
      if (empty.length > 0) problems.push(`${empty.length} empty`);

      if (problems.length > 0) {
        const kept = accepted.length;
        toast.warning(
          kept > 0
            ? `Attached ${kept} file${kept === 1 ? '' : 's'} — skipped the rest`
            : "Couldn't attach those files",
          {
            description: `Skipped: ${problems.join('; ')}.`,
            duration: 8000,
          },
        );
      }

      if (accepted.length === 0) return prev;

      const next: PendingAttachment[] = accepted.map((f) => ({
        localId: crypto.randomUUID(),
        file: f,
        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
        uploadedUrl: null,
        state: 'idle',
      }));
      return [...prev, ...next];
    });
  }, []);

  const remove = useCallback((localId: string) => {
    setItems((prev) => {
      const found = prev.find((p) => p.localId === localId);
      if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }, []);

  const clear = useCallback(() => {
    setItems((prev) => {
      for (const p of prev) {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      }
      return [];
    });
  }, []);

  // Paste handler — pulls images + files off the clipboardData. Caller hooks
  // it onto the textarea's onPaste so screenshots and copy/pasted images Just
  // Work.
  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      const items = e.clipboardData?.items ?? [];
      const files: File[] = [];
      for (const it of Array.from(items)) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        // Don't preventDefault unless we actually have files — we want plain
        // text paste to keep working unchanged.
        e.preventDefault();
        add(files);
      }
    },
    [add],
  );

  // Drag/drop handler — same idea. Caller wraps the composer in a div with
  // these handlers.
  const dragHandlers = {
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
      }
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      e.preventDefault();
      add(files);
    },
  };

  return {
    items,
    add,
    remove,
    clear,
    onPaste,
    dragHandlers,
    maxFiles: MAX_FILES,
    maxFileBytes: MAX_FILE_BYTES,
  };
}

export function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
